import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Home.css";
import { useCookies } from "react-cookie";
import { getUserAllowedPlants } from "./services/PlantServices";
import {
  getDashboardDetails,
  getSalesList,
  getPurchaseList,
  getQAQCDashBoardList,
} from "./services/DashboardServices";
import { getBgImageLogo } from "./services/LoginServices";
import { displayErrorToast } from "./helpers";
import ErrorToast from "./ErrorToast";
import { getStockStatement } from "./services/ConsumptionNoteServices";
import { Table } from "antd";
import moment from "moment";
import Sidebar from "./Sidebar";
import MarketingSalesChart from "./components/MarketingSalesChart";
import PurchaseChart from "./components/PurchaseChart";
import SettingsIcon from "./components/SettingsIcon";
import {
  loadDashboardSettings,
  getDateRangeFromDashboardSettings,
  resolveDashboardPlants,
  getDashboardSettingsSummary,
  DASHBOARD_DATE_MODES,
} from "./utils/dashboardSettings";

const Cards = ({ day, yesterday, week, month, year }) => (
  <div className="cards-container">
    <div className="card" id="dayCard">
      <span className="card-title">Today</span>
      <p>{(day || 0).toFixed(2)}</p>
    </div>
    <div className="card" id="yesterdayCard">
      <span className="card-title">Yesterday</span>
      <p>{(yesterday || 0).toFixed(2)}</p>
    </div>
    <div className="card" id="weekCard">
      <span className="card-title">Week</span>
      <p>{(week || 0).toFixed(2)}</p>
    </div>
    <div className="card" id="monthCard">
      <span className="card-title">Month</span>
      <p>{(month || 0).toFixed(2)}</p>
    </div>
    <div className="card" id="yearCard">
      <span className="card-title">Year</span>
      <p>{(year || 0).toFixed(2)}</p>
    </div>
  </div>
);

const renderCsrStatus = (status) => {
  const normalized = String(status || "Pending").toLowerCase();
  const isCompleted = normalized === "completed";
  return (
    <span
      style={{
        backgroundColor: isCompleted ? "#16a085" : "#e67e22",
        color: "white",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: "bold",
        display: "inline-block",
        minWidth: 80,
      }}
    >
      {status || "Pending"}
    </span>
  );
};

const HomeTabs = ({
  scheduleData,
  salesData,
  purchaseData,
  qaqcData,
  qaqcLoading,
  stockData,
  stockAsOnDate,
  stockViewMode = "daily",
  enabledTabs = ["schedule", "sales", "purchase", "qaqc", "stock"],
  defaultTab = "schedule",
  qaqcType = "ALL",
  viewDate,
  viewToDate,
  dateMode,
  onDateChange,
  onTabChange,
  onQaqcTypeChange,
  cookies,
  plantId,
}) => {
  const visibleTabs = enabledTabs.length ? enabledTabs : ["schedule"];
  const [activeTab, setActiveTab] = useState(
    visibleTabs.includes(defaultTab) ? defaultTab : visibleTabs[0]
  );
  const [currentDate, setCurrentDate] = useState(() => moment(viewDate || undefined));
  const isDateRangeMode = dateMode === DASHBOARD_DATE_MODES.DATE_RANGE;
  const showsRangeLabel = isDateRangeMode || dateMode === DASHBOARD_DATE_MODES.LAST_TWO_MONTHS;
  const rangeEndDate = moment(viewToDate || viewDate || undefined);

  useEffect(() => {
    setActiveTab((current) => (
      visibleTabs.includes(current)
        ? current
        : (visibleTabs.includes(defaultTab) ? defaultTab : visibleTabs[0])
    ));
  }, [defaultTab, enabledTabs.join(",")]);

  useEffect(() => {
    if (activeTab === "stock" && stockAsOnDate) {
      setCurrentDate(moment(stockAsOnDate, "DD-MM-YYYY", true));
    } else if (activeTab !== "stock" && viewDate) {
      setCurrentDate(moment(viewDate));
    }
  }, [viewDate, stockAsOnDate, activeTab]);

  const notifyDateChange = (newDate, endDate = null) => {
    onDateChange && onDateChange(newDate, activeTab, qaqcType, endDate);
  };

  const handlePrev = () => {
    if (isDateRangeMode) {
      const nextFrom = currentDate.clone().subtract(1, "day");
      const nextTo = rangeEndDate.clone().subtract(1, "day");
      setCurrentDate(nextFrom);
      notifyDateChange(nextFrom, nextTo);
      return;
    }
    const newDate = currentDate.clone().subtract(1, "day");
    setCurrentDate(newDate);
    notifyDateChange(newDate);
  };

  const handleNext = () => {
    if (isDateRangeMode) {
      const nextFrom = currentDate.clone().add(1, "day");
      const nextTo = rangeEndDate.clone().add(1, "day");
      setCurrentDate(nextFrom);
      notifyDateChange(nextFrom, nextTo);
      return;
    }
    const newDate = currentDate.clone().add(1, "day");
    setCurrentDate(newDate);
    notifyDateChange(newDate);
  };

  const handleToday = () => {
    const newDate = moment();
    setCurrentDate(newDate);
    notifyDateChange(newDate, isDateRangeMode ? newDate.clone() : null);
  };

  const lblTotalQty =
    activeTab === "sales"
      ? (salesData || []).reduce((sum, rec) => sum + (Number(rec.qty) || 0), 0)
      : activeTab === "purchase"
      ? (purchaseData || []).reduce((sum, rec) => sum + (Number(rec.received_qty) || 0), 0)
      : activeTab === "schedule"
      ? (scheduleData || []).reduce(
          (sum, rec) => sum + (Number(rec.scheduled_qty) || 0),
          0
        )
      : activeTab === "qaqc"
      ? (qaqcData || []).reduce((sum, rec) => sum + (Number(rec.qty) || 0), 0)
      : activeTab === "stock"
      ? (stockData || []).reduce(
          (sum, rec) => sum + (Number(rec.closing_qty) || 0),
          0
        )
      : 0;

  const dateLabel = showsRangeLabel && rangeEndDate.isValid()
    ? `${currentDate.format("DD-MM-YYYY")} to ${rangeEndDate.format("DD-MM-YYYY")}`
    : currentDate.format("DD-MM-YYYY");

  const getStatusColor = (status) => {
    if (!status) return "#95a5a6";
    const s = String(status).toLowerCase();
    if (s.includes("not started")) return "#95a5a6";
    if (s.includes("in progress")) return "#3498db";
    if (s.includes("to complete")) return "#e67e22";
    if (s.includes("completed")) return "#16a085";
    if (s.includes("pending")) return "#e67e22";
    return "#7f8c8d";
  };

  const totalBtnStyle = {
    fontWeight: 700,
    background:
      activeTab === "sales"
        ? "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
        : activeTab === "purchase"
        ? "linear-gradient(135deg, #e67e22 0%, #d35400 100%)"
        : activeTab === "stock"
        ? "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)"
        : "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
    color: "white",
    border: "none",
  };

  const totalLabel =
    activeTab === "stock"
      ? `${lblTotalQty.toFixed(3)} units`
      : activeTab === "purchase"
      ? `${lblTotalQty.toFixed(2)}`
      : `${lblTotalQty.toFixed(2)} m³`;

  return (
    <div className="dashboard-tabs-wrap">
      <div className="dashboard-toolbar">
        <div className="dashboard-toolbar-tabs">
          {visibleTabs.includes("schedule") && (
          <button
            className={`custom-tab ${activeTab === "schedule" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("schedule");
              onTabChange && onTabChange("schedule");
            }}
          >
            Schedule
          </button>
          )}
          {visibleTabs.includes("sales") && (
          <button
            className={`custom-tab ${activeTab === "sales" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("sales");
              onTabChange && onTabChange("sales");
            }}
          >
            Sales
          </button>
          )}
          {visibleTabs.includes("purchase") && (
          <button
            className={`custom-tab ${activeTab === "purchase" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("purchase");
              onTabChange && onTabChange("purchase");
            }}
          >
            Purchase
          </button>
          )}
          {visibleTabs.includes("qaqc") && (
          <button
            className={`custom-tab ${activeTab === "qaqc" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("qaqc");
              onTabChange && onTabChange("qaqc");
            }}
          >
            QAQC
          </button>
          )}
          {visibleTabs.includes("stock") && (
          <button
            className={`custom-tab ${activeTab === "stock" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("stock");
              onTabChange && onTabChange("stock");
            }}
          >
            Stock
            <span className="stock-live-badge">LIVE</span>
          </button>
          )}
        </div>

        <div className="dashboard-toolbar-actions">
          <button className="nav-btn nav-btn-total" style={totalBtnStyle}>
            {totalLabel}
          </button>
          <button className="nav-btn nav-btn-date" type="button">
            {dateLabel}
          </button>
          <button className="nav-btn" type="button" onClick={handlePrev}>
            Prev
          </button>
          <button className="nav-btn" type="button" onClick={handleToday}>
            Today
          </button>
          <button className="nav-btn" type="button" onClick={handleNext}>
            Next
          </button>
        </div>
      </div>

      {activeTab === "schedule" && (
        <Table
          dataSource={scheduleData}
          pagination={{ pageSize: 12, size: "small" }}
          rowKey="__key"
          size="small"
          tableLayout="fixed"
          columns={[
            { title: "Job No", dataIndex: "schedule_no", width: "6%", align: "center", defaultSortOrder: "descend", sorter: (a, b) => (Number(a.schedule_no) || 0) - (Number(b.schedule_no) || 0) },
            { title: "Date", dataIndex: "delivery_date", width: "8%", align: "center", sorter: (a, b) => String(a.delivery_date || "").localeCompare(String(b.delivery_date || "")) },
            { title: "Time", dataIndex: "delivery_time", width: "7%", align: "center", sorter: (a, b) => String(a.delivery_time || "").localeCompare(String(b.delivery_time || "")) },
            {
              title: "Customer",
              dataIndex: "customer_name",
              width: "15%",
              align: "left",
              ellipsis: true,
            },
            {
              title: "Site",
              dataIndex: "project_name",
              width: "13%",
              align: "left",
              ellipsis: true,
            },
            { title: "Grade", dataIndex: "grade", width: "6%", align: "center", ellipsis: true },
            {
              title: "Delivery",
              dataIndex: "delivery_mode",
              width: "8%",
              align: "center",
              render: (text) => text?.toUpperCase(),
            },
            {
              title: "Schedule Qty",
              dataIndex: "scheduled_qty",
              width: "9%",
              align: "right",
              render: (v) => (v == null ? "-" : Number(v).toFixed(2)),
            },
            {
              title: "Achieved Qty",
              dataIndex: "supplied_qty",
              width: "9%",
              align: "right",
              render: (v) => (v == null ? "-" : Number(v).toFixed(2)),
            },
            {
              title: "Status",
              dataIndex: "status",
              width: "11%",
              align: "center",
              ellipsis: true,
              render: (text) => (
                <span
                  style={{
                    backgroundColor: getStatusColor(text),
                    color: "white",
                    padding: "1px 6px",
                    borderRadius: 6,
                    display: "inline-block",
                    fontSize: 11,
                    lineHeight: 1.3,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {text || "-"}
                </span>
              ),
            },
          ]}
          className="custom-table schedule-fit-table"
        />
      )}

      {activeTab === "sales" && (
        <>
          <MarketingSalesChart
            salesData={salesData}
            cookies={cookies}
            plantId={plantId}
            referenceDate={currentDate || viewDate}
            period={dateLabel}
          />
          <Table
            className="custom-table"
            dataSource={salesData}
            pagination={{ pageSize: 10 }}
            rowKey="dc_no"
            columns={[
              { title: "DC No", dataIndex: "dc_no", width: 90, align: "center" },
              { title: "Date", dataIndex: "date", align: "center" },
              { title: "Time", dataIndex: "time", align: "center" },
              { title: "Customer", dataIndex: "customer", width: 200, align: "center" },
              { title: "Site", dataIndex: "site" },
              { title: "Grade", dataIndex: "grade" },
              { title: "Qty.", dataIndex: "qty", align: "right" },
              {
                title: "Trip - Qty",
                align: "center",
                width: 180,
                render: (_, record) => (
                  <span>{`${record.cumulative_sl_no || 0} - ${(record.cumulative_qty || 0).toFixed(2)}`}</span>
                ),
              },
              {
                title: "Delivery",
                dataIndex: "delivery_mode",
                align: "center",
                render: (text) => text?.toUpperCase(),
              },
              { title: "Vehicle No.", dataIndex: "vehiclenop", align: "center" },
              { title: "CSM", dataIndex: "marketing" },
              { title: "Job #", dataIndex: "job_no", align: "center" },
              { title: "SO #", dataIndex: "so_no", align: "center" },
            ]}
          />
        </>
      )}

      {activeTab === "purchase" && (
        <>
          <PurchaseChart
            purchaseData={purchaseData}
            cookies={cookies}
            plantId={plantId}
            referenceDate={currentDate || viewDate}
            period={dateLabel}
          />
          <Table
            className="custom-table"
            dataSource={purchaseData}
            pagination={{ pageSize: 10 }}
            rowKey={(record) => `${record.grn_no}_${record.item}_${record.dc_no}`}
            columns={[
              { title: "GRN No", dataIndex: "grn_no", align: "center" },
              { title: "Date", dataIndex: "date", align: "center" },
              { title: "Time", dataIndex: "sl_time", align: "center" },
              { title: "DC No", dataIndex: "dc_no", align: "center" },
              { title: "DC Date", dataIndex: "dc_dt", align: "center" },
              { title: "Vendor", dataIndex: "vendor", align: "center" },
              { title: "Materials", dataIndex: "item", align: "center" },
              { title: "DC Qty", dataIndex: "dc_qty", align: "right" },
              { title: "Received Qty", dataIndex: "received_qty", align: "right" },
              { title: "Vehicle", dataIndex: "vehicle", align: "center" },
            ]}
          />
        </>
      )}

      {activeTab === "qaqc" && (
        <>
          <div style={{ marginBottom: 16, display: "flex", gap: 10 }}>
            {["ALL", "7", "28"].map((t) => (
              <button
                key={t}
                className={`btn btn-sm ${
                  qaqcType === t ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => {
                  onQaqcTypeChange && onQaqcTypeChange(t);
                }}
              >
                {t === "ALL" ? "ALL" : `${t}th Day`}
              </button>
            ))}
          </div>

          <Table
            className="custom-table"
            loading={qaqcLoading}
            dataSource={qaqcData}
            rowKey={(r) =>
              `${r.date}-${r.customer}-${r.site}-${r.grade}-${r.delivery_mode}`
            }
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: qaqcLoading
                ? "Loading QAQC..."
                : "No QAQC data available",
            }}
            columns={[
              { title: "Date", dataIndex: "date", align: "center" },
              {
                title: "Customer",
                dataIndex: "customer",
                align: "center",
                render: (text) => <div style={{ textAlign: "left" }}>{text}</div>,
              },
              {
                title: "Site",
                dataIndex: "site",
                align: "center",
                render: (text) => <div style={{ textAlign: "left" }}>{text}</div>,
              },
              { title: "Grade", dataIndex: "grade", align: "center" },
              { title: "Qty", dataIndex: "qty", align: "right" },
              { title: "Delivery", dataIndex: "delivery_mode", align: "center" },
              {
                title: "7th Day (CSR)",
                dataIndex: "due_7_date",
                align: "center",
                render: (_, record) => (
                  <div>
                    <div>{record.due_7_date}</div>
                    {renderCsrStatus(record.status_7)}
                  </div>
                ),
              },
              {
                title: "28th Day (CSR)",
                dataIndex: "due_28_date",
                align: "center",
                render: (_, record) => (
                  <div>
                    <div>{record.due_28_date}</div>
                    {renderCsrStatus(record.status_28)}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}

      {activeTab === "stock" && (
        <Table
          className="custom-table"
          dataSource={stockData}
          pagination={{ pageSize: 15 }}
          rowKey={(r) => r.product_id}
          locale={{
            emptyText: "No stock movement recorded for the selected plant and date.",
          }}
          title={() => (
            <span>
              Online Stock Statement
              {stockAsOnDate
                ? stockViewMode === "daily"
                  ? ` for ${stockAsOnDate}`
                  : ` (as on ${stockAsOnDate})`
                : ""}
              <span className="stock-live-badge">AUTO-REFRESH</span>
            </span>
          )}
          columns={[
            { title: "Material", dataIndex: "product_name", align: "left" },
            { title: "Unit", dataIndex: "unit", width: 80, align: "center" },
            ...(stockViewMode === "daily"
              ? [
                  {
                    title: "Opening",
                    dataIndex: "opening_qty",
                    align: "right",
                    render: (v) => Number(v || 0).toFixed(3),
                  },
                  {
                    title: "Received",
                    dataIndex: "received_qty",
                    align: "right",
                    render: (v) => Number(v || 0).toFixed(3),
                  },
                  {
                    title: "Consumption",
                    dataIndex: "consumed_qty",
                    align: "right",
                    render: (v) => Number(v || 0).toFixed(3),
                  },
                  {
                    title: "Closing",
                    dataIndex: "closing_qty",
                    align: "right",
                    render: (v) => (
                      <strong style={{ color: Number(v) < 0 ? "#e74c3c" : "#2c3e50" }}>
                        {Number(v || 0).toFixed(3)}
                      </strong>
                    ),
                  },
                ]
              : [
                  {
                    title: "Received (GRN)",
                    dataIndex: "received_qty",
                    align: "right",
                    render: (v) => Number(v || 0).toFixed(3),
                  },
                  {
                    title: "Consumed",
                    dataIndex: "consumed_qty",
                    align: "right",
                    render: (v) => Number(v || 0).toFixed(3),
                  },
                  {
                    title: "Closing Stock",
                    dataIndex: "closing_qty",
                    align: "right",
                    render: (v) => (
                      <strong style={{ color: Number(v) < 0 ? "#e74c3c" : "#2c3e50" }}>
                        {Number(v || 0).toFixed(3)}
                      </strong>
                    ),
                  },
                ]),
          ]}
        />
      )}
    </div>
  );
};

const Home = () => {
  const [cookies] = useCookies(["myToken", "myUserId"]);
  const [salesData, setSalesData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [qaqcData, setQAQCData] = useState([]);
  const [qaqcLoading, setQAQCLoading] = useState(false);
  const [qaqcType, setQaqcType] = useState("ALL");
  const [filteredData, setFilteredData] = useState([]);
  const [stockStatement, setStockStatement] = useState([]);
  const [stockViewMode, setStockViewMode] = useState("daily");
  const [stockAsOnDate, setStockAsOnDate] = useState(() =>
    getDateRangeFromDashboardSettings(loadDashboardSettings(cookies.myUserId))[1]
  );
  const [day, setDay] = useState(0);
  const [yesterday, setYesterday] = useState(0);
  const [week, setWeek] = useState(0);
  const [month, setMonth] = useState(0);
  const [year, setYear] = useState(0);
  const [inputs, setInputs] = useState({});
  const [plants, setPlants] = useState([]);
  const [allowedPlants, setAllowedPlants] = useState([]);
  const [dashboardSettings, setDashboardSettings] = useState(() =>
    loadDashboardSettings(cookies.myUserId)
  );

  const activeTabRef = useRef(dashboardSettings.defaultTab || "schedule");
  const requestSeq = useRef(0);

  const [fromDate, setFromDate] = useState(() =>
    getDateRangeFromDashboardSettings(loadDashboardSettings(cookies.myUserId))[0]
  );
  const [toDate, setToDate] = useState(() =>
    getDateRangeFromDashboardSettings(loadDashboardSettings(cookies.myUserId))[1]
  );

  const settingsSummary = useMemo(
    () => getDashboardSettingsSummary(dashboardSettings, allowedPlants),
    [dashboardSettings, allowedPlants]
  );

  const applyDashboardSettings = (settings, plantOptions) => {
    const { plants: filteredPlants, defaultPlantId } = resolveDashboardPlants(
      settings,
      plantOptions
    );
    setPlants(filteredPlants);
    const [from, to] = getDateRangeFromDashboardSettings(settings);
    setFromDate(from);
    setToDate(to);
    setStockAsOnDate(to);
    if (defaultPlantId) {
      const plantId = Number(defaultPlantId);
      const tab = settings.defaultTab || activeTabRef.current || "schedule";
      activeTabRef.current = tab;
      setInputs({ plant_id: plantId });
      handleDashboard(
        plantId,
        from.format("DD-MM-YYYY"),
        to.format("DD-MM-YYYY"),
        { tab }
      );
    }
  };

  const loadStock = async (plantId, asOnDate, mode = "daily", silent = false) => {
    if (!plantId || !asOnDate) return;
    const asOnM = toMomentDate(asOnDate);
    if (!asOnM.isValid()) return;
    try {
      const res = await getStockStatement(
        cookies,
        Number(plantId),
        asOnM.format("DD-MM-YYYY"),
        mode,
        { silent }
      );
      setStockViewMode(res?.mode || mode);
      setStockStatement(res?.stock_statement || []);
    } catch (e) {
      console.error("Stock load failed", e);
      setStockStatement([]);
    }
  };

  const toMomentDate = (value) => {
    if (moment.isMoment(value)) {
      return value.clone();
    }
    return moment(value, "DD-MM-YYYY", true);
  };

  const loadQAQC = async (plantId, from, to, type = "ALL", silent = false) => {
    if (!plantId || !from || !to) return;
    const fromM = toMomentDate(from);
    const toM = toMomentDate(to);
    if (!fromM.isValid() || !toM.isValid()) {
      console.error("QAQC load skipped: invalid date", { from, to });
      return;
    }
    try {
      setQAQCLoading(true);
      const res = await getQAQCDashBoardList(
        cookies,
        plantId,
        fromM.format("DD-MM-YYYY"),
        toM.format("DD-MM-YYYY"),
        type,
        { silent }
      );
      setQAQCData(res?.results || []);
    } catch (e) {
      console.error("QAQC load failed", e);
      setQAQCData([]);
      displayErrorToast(
        e?.response?.data?.error
        || e?.response?.data?.detail
        || "Failed to load QAQC data"
      );
    } finally {
      setQAQCLoading(false);
    }
  };

  const applySales = (salesResponse) => {
    setSalesData(
      (salesResponse?.sales_list || []).sort((a, b) => {
        const dateA = new Date(
          String(a.date || "").split("-").reverse().join("-") + " " + (a.time || "")
        );
        const dateB = new Date(
          String(b.date || "").split("-").reverse().join("-") + " " + (b.time || "")
        );
        return dateB - dateA;
      })
    );
  };

  const loadActiveTabData = async (tab, plantId, fromDateStr, toDateStr, silent, seq, type = qaqcType) => {
    if (tab === "sales") {
      const salesResponse = await getSalesList(cookies, plantId, fromDateStr, toDateStr, { silent });
      if (seq !== requestSeq.current) return;
      applySales(salesResponse);
      return;
    }
    if (tab === "purchase") {
      const purchaseResponse = await getPurchaseList(cookies, plantId, fromDateStr, toDateStr, { silent });
      if (seq !== requestSeq.current) return;
      setPurchaseData(purchaseResponse?.purchase_list || []);
      return;
    }
    if (tab === "qaqc") {
      await loadQAQC(plantId, fromDateStr, toDateStr, type, silent);
      return;
    }
    if (tab === "stock") {
      await loadStock(plantId, toDateStr, "daily", silent);
    }
  };

  const handleDashboard = async (plant_id, fromDateStr, toDateStr, options = {}) => {
    const plantId = Number(plant_id);
    if (!plantId) return;
    const silent = Boolean(options.silent);
    const tab = options.tab || activeTabRef.current || "schedule";
    const type = options.qaqcType || qaqcType;
    const seq = ++requestSeq.current;

    try {
      const dashboardPromise = getDashboardDetails(cookies, plantId, fromDateStr, toDateStr, { silent });
      const tabPromise = tab === "schedule"
        ? null
        : loadActiveTabData(tab, plantId, fromDateStr, toDateStr, silent, seq, type).catch((err) => {
            if (seq === requestSeq.current) {
              console.error("Failed to load dashboard tab:", err);
            }
          });

      const dashboardResponse = await dashboardPromise;
      if (seq !== requestSeq.current) return;

      setFilteredData(dashboardResponse?.work_schedule_list || []);
      setDay(dashboardResponse?.today_sum || 0);
      setYesterday(dashboardResponse?.yesterday_sum || 0);
      setWeek(dashboardResponse?.week_sum || 0);
      setMonth(dashboardResponse?.month_sum || 0);
      setYear(dashboardResponse?.year_sum || 0);

      if (tabPromise) {
        await tabPromise;
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error("Failed to load dashboard:", err);
    }
  };

  const handleDateChangeFromTabs = (newMomentInput, activeTab, typeFilter = qaqcType, endMomentInput = null) => {
    const m = moment.isMoment(newMomentInput)
      ? newMomentInput
      : moment(newMomentInput);
    const endM = endMomentInput
      ? (moment.isMoment(endMomentInput) ? endMomentInput : moment(endMomentInput))
      : m;

    const selectedPlantId = Number(inputs.plant_id || (plants[0] && plants[0].value));
    if (!selectedPlantId) return;

    if (activeTab) {
      activeTabRef.current = activeTab;
    }

    if (activeTab === "stock") {
      setStockAsOnDate(m);
      loadStock(selectedPlantId, m);
      return;
    }

    const fromStr = m.format("DD-MM-YYYY");
    const toStr = endM.format("DD-MM-YYYY");
    handleDashboard(selectedPlantId, fromStr, toStr, { tab: activeTab, qaqcType: typeFilter });
    setFromDate(m);
    setToDate(endM);
  };

  const handleQaqcTypeChange = (type) => {
    setQaqcType(type);
    const selectedPlantId = inputs.plant_id || (plants[0] && plants[0].value);
    if (!selectedPlantId) return;
    loadQAQC(selectedPlantId, fromDate, toDate, type);
  };

  useEffect(() => {
    getBgImageLogo().then((res) => {
      try {
        const active = (res?.company_list || []).find((c) => c.status);
        if (active) document.title = active.name;
      } catch (e) {}
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchPlantsAndData = () => {
      getUserAllowedPlants(cookies)
        .then((res) => {
          const plantOptions = (res?.plant_list || [])
            .filter((p) => p.status)
            .map((p) => ({
              value: p.id,
              label: p.plant_alias,
            }));
          setAllowedPlants(plantOptions);
          applyDashboardSettings(dashboardSettings, plantOptions);
        })
        .catch((e) => console.error(e));
    };

    fetchPlantsAndData();
  }, [cookies.myUserId]);

  useEffect(() => {
    const onSettingsUpdated = (event) => {
      const nextSettings = event.detail || loadDashboardSettings(cookies.myUserId);
      setDashboardSettings(nextSettings);
      getUserAllowedPlants(cookies)
        .then((res) => {
          const plantOptions = (res?.plant_list || [])
            .filter((p) => p.status)
            .map((p) => ({ value: p.id, label: p.plant_alias }));
          setAllowedPlants(plantOptions);
          applyDashboardSettings(nextSettings, plantOptions);
        })
        .catch((e) => console.error(e));
    };

    window.addEventListener("dashboardSettingsUpdated", onSettingsUpdated);
    return () => window.removeEventListener("dashboardSettingsUpdated", onSettingsUpdated);
  }, [cookies]);

  useEffect(() => {
    if (!dashboardSettings.autoRefresh) return undefined;

    const interval = setInterval(() => {
      const plantId = Number(inputs.plant_id || (plants[0] && plants[0].value));
      if (!plantId) return;

      handleDashboard(
        plantId,
        fromDate.format("DD-MM-YYYY"),
        toDate.format("DD-MM-YYYY"),
        { silent: true, tab: activeTabRef.current }
      );
    }, 20000);

    return () => clearInterval(interval);
  }, [
    dashboardSettings.autoRefresh,
    cookies,
    inputs.plant_id,
    plants,
    fromDate,
    toDate,
    stockAsOnDate,
  ]);

  const flattenedSchedule = useMemo(() => {
    if (!Array.isArray(filteredData)) return [];
    const rows = filteredData.flatMap((group, gIndex) => {
      const entries = Array.isArray(group.entries) ? group.entries : [];
      return entries.map((entry, idx) => ({
        ...entry,
        delivery_date: group.delivery_date,
        __key: `${group.delivery_date}_${entry.schedule_no || idx}_${gIndex}`,
      }));
    });
    return rows.sort((a, b) => {
      const noA = Number(a.schedule_no) || 0;
      const noB = Number(b.schedule_no) || 0;
      if (noB !== noA) return noB - noA;
      const dateA = String(a.delivery_date || "").split("-").reverse().join("-");
      const dateB = String(b.delivery_date || "").split("-").reverse().join("-");
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      return String(b.delivery_time || "").localeCompare(String(a.delivery_time || ""));
    });
  }, [filteredData]);

  return (
    <>
      <ErrorToast />
      <div id="outer-container" className="App">
        <Sidebar pageWrapId={"page-wrap"} outerContainerId={"outer-container"} />
        <div id="page-wrap" className="dashboard-home-wrap">
        <div className="dashboard-page">
        <div className="dashboard-settings-summary">
          <div className="dashboard-settings-summary-text">
            <span className="dashboard-settings-summary-label">Dashboard:</span>
            <span>{settingsSummary}</span>
            <span className="dashboard-settings-summary-hint">
              Use the settings icon to change plant, date range, and tabs.
            </span>
          </div>
          <div className="dashboard-settings-summary-actions">
            <SettingsIcon
              onClickHandler={() => {
                window.dispatchEvent(new CustomEvent("openContextualSettings"));
              }}
              variant="inline"
            />
          </div>
        </div>

        <div id="salesDashboard">
          <Cards
            day={day}
            yesterday={yesterday}
            week={week}
            month={month}
            year={year}
          />

          <HomeTabs
            scheduleData={flattenedSchedule}
            salesData={salesData}
            purchaseData={purchaseData}
            qaqcData={qaqcData}
            qaqcLoading={qaqcLoading}
            stockData={stockStatement}
            stockViewMode={stockViewMode}
            stockAsOnDate={
              moment.isMoment(stockAsOnDate)
                ? stockAsOnDate.format("DD-MM-YYYY")
                : toMomentDate(stockAsOnDate).format("DD-MM-YYYY")
            }
            enabledTabs={dashboardSettings.enabledTabs}
            defaultTab={dashboardSettings.defaultTab}
            qaqcType={qaqcType}
            viewDate={fromDate}
            viewToDate={toDate}
            dateMode={dashboardSettings.dateMode}
            cookies={cookies}
            plantId={Number(inputs.plant_id || (plants[0] && plants[0].value)) || null}
            onDateChange={handleDateChangeFromTabs}
            onQaqcTypeChange={handleQaqcTypeChange}
            onTabChange={(tab, type = "ALL") => {
              activeTabRef.current = tab;
              const plantId = Number(inputs.plant_id || (plants[0] && plants[0].value));
              if (!plantId) return;
              const fromStr = fromDate.format("DD-MM-YYYY");
              const toStr = toDate.format("DD-MM-YYYY");
              const seq = ++requestSeq.current;
              if (tab === "qaqc") {
                setQaqcType(type);
                loadQAQC(plantId, fromDate, toDate, type);
              } else if (tab === "stock") {
                loadStock(plantId, stockAsOnDate || toDate);
              } else if (tab === "sales" || tab === "purchase") {
                loadActiveTabData(tab, plantId, fromStr, toStr, false, seq);
              }
            }}
          />
        </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Home;
